import { useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Button } from '@/common/components/Button/Button';
import { ControlledMoneyField } from '@/common/components/Fields/ControlledMoneyField/ControlledMoneyField';
import { ControlledRadioButtonGroup } from '@/common/components/Fields/ControlledRadioButtonGroup/ControlledRadioButtonGroup';
import { ControlledTextField } from '@/common/components/Fields/ControlledTextField/ControlledTextField';
import { Form } from '@/common/components/Form/Form';
import { Modal } from '@/common/components/Modal/Modal';
import { TOverlayRef } from '@/common/components/Overlay/Overlay.types';
import { Stack } from '@/common/components/Stack/Stack';
import {
  useCreateEmployeeTaxProfile,
  useUpdateEmployeeTaxProfile,
} from '@/common/integrations/menhir/employee/employee/employee.hooks';
import { IEmployeeTaxProfile } from '@/common/integrations/menhir/employee/employee/employee.types';
import { notify } from '@/common/utils/toast/toast';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  TTaxProfileFormSchema,
  taxProfileFormSchema,
} from './TaxProfileFormModal.schema';

interface IAddressFormModalProps {
  profile?: IEmployeeTaxProfile;
  onClose: () => void;
}

export const TaxProfileFormModal = ({
  profile,
  onClose,
}: IAddressFormModalProps) => {
  const { t } = useTranslation();
  const modalRef = useRef<TOverlayRef>(null);
  const methods = useForm<TTaxProfileFormSchema>({
    mode: 'onTouched',
    defaultValues: {
      maritalStatus: profile?.maritalStatus,
      dependents: profile?.dependents,
      salary: profile?.salary,
    },
    resolver: zodResolver(taxProfileFormSchema),
  });

  const { mutateAsync: createTaxProfile } = useCreateEmployeeTaxProfile();
  const { mutateAsync: updateTaxProfile } = useUpdateEmployeeTaxProfile();

  const onSubmit = async (form: TTaxProfileFormSchema) => {
    const mutateAsync = !!profile ? updateTaxProfile : createTaxProfile;
    try {
      await mutateAsync({
        maritalStatus: form.maritalStatus,
        dependents: form.dependents,
        salary: form.salary,
      });
      notify({
        message: t('dashboard.tax_profile.success_message'),
        type: 'success',
      });
      modalRef.current?.close();
    } catch (err) {
      notify({
        type: 'error',
        message: t('errors.api.generic'),
      });
    }
  };

  const maritalStatuses = [
    {
      label: t('dashboard.tax_profile.marital_status.not_married'),
      value: 'not_married',
    },
    {
      label: t('dashboard.tax_profile.marital_status.married_singular'),
      value: 'married_singular',
    },
    {
      label: t('dashboard.tax_profile.marital_status.married_joint'),
      value: 'married_joint',
    },
  ];

  return (
    <Modal
      title={t('dashboard.tax_profile.title')}
      onClose={() => onClose()}
      modalRef={modalRef}
      footer={({ close }) => (
        <Stack horizontal gap="md">
          <Button
            variant="secondary"
            onPress={close}
            disabled={methods.formState.isSubmitting}
          >
            {t('dashboard.personal_info.cancel_button')}
          </Button>
          <Button
            type="submit"
            className="flex-1"
            loading={methods.formState.isSubmitting}
            onPress={methods.handleSubmit(onSubmit)}
          >
            {t('dashboard.personal_info.save_button')}
          </Button>
        </Stack>
      )}
    >
      <Stack gap="section">
        <BodyText color="neutral20" size="sm">
          {t('dashboard.tax_profile.form.tooltip_message')}
        </BodyText>
        <FormProvider {...methods}>
          <Form onSubmit={methods.handleSubmit(onSubmit)}>
            <Stack gap="md">
              <ControlledRadioButtonGroup
                title={t('dashboard.tax_profile.fields.marital_status')}
                options={maritalStatuses}
                name="maritalStatus"
              />
              <ControlledTextField
                inputFormatter={(value) => value.replace(/[^\d]/g, '')}
                name="dependents"
                label={t('dashboard.tax_profile.fields.dependents')}
              />
              <ControlledMoneyField
                name="salary"
                label={t('dashboard.tax_profile.fields.salary_question')}
                size="sm"
              />
            </Stack>
          </Form>
        </FormProvider>
      </Stack>
    </Modal>
  );
};
